/**
 * sectionOrder — reorder page sections without a drag.
 *
 * The page grid is a vertical stack of section blocks (order = y). Dragging a
 * block by its label tab reorders it, but a drag is the one gesture WCAG 2.2
 * (SC 2.5.7) says must have a single-pointer alternative, and on a phone a
 * long-press-then-drag past the viewport edge is hard to complete. "Move up"
 * / "Move down" swap a block with its neighbour by rewriting both y values —
 * pure arithmetic here, applied through the store by the caller.
 */
import type { GridItem } from "@fredericrous/grid";

export interface SectionMove {
  readonly id: string;
  readonly y: number;
}

/** Section blocks of a grid, top to bottom. */
export function orderedSections(
  items: Readonly<Record<string, GridItem>>,
): GridItem[] {
  return Object.values(items)
    .filter((it) => it.contentType === "grid")
    .sort((a, b) => a.position.y - b.position.y);
}

/**
 * The y rewrites that swap `itemId` with its neighbour in `direction`, or
 * null when it is already first/last. Two writes, never one: the neighbour
 * must vacate the row the moved block lands on, else compaction resolves the
 * overlap by its own (y, x) tie-break and may leave the order unchanged.
 */
export function swapSection(
  items: Readonly<Record<string, GridItem>>,
  itemId: string,
  direction: "up" | "down",
): readonly SectionMove[] | null {
  const sections = orderedSections(items);
  const idx = sections.findIndex((it) => it.id === itemId);
  if (idx < 0) return null;
  const me = sections[idx]!;
  const other = sections[direction === "up" ? idx - 1 : idx + 1];
  if (!other) return null;
  const top = direction === "up" ? me : other;
  const bottom = direction === "up" ? other : me;
  const topY = Math.min(me.position.y, other.position.y);
  return [
    { id: top.id, y: topY },
    { id: bottom.id, y: topY + top.position.h },
  ];
}

/** Can `itemId` move in `direction`? (Drives the buttons' disabled state.) */
export function canMoveSection(
  items: Readonly<Record<string, GridItem>>,
  itemId: string,
  direction: "up" | "down",
): boolean {
  return swapSection(items, itemId, direction) != null;
}
